import { weatherState } from '../fx/ambientFx';
import { setCarClearcoat } from '../vehicles/carShader';
import { defaultVehicleMeshFactory, lodCamera, lodView, vehicleAmbient, type VehicleMeshFactory } from '../vehicles/vehicleView';
import { moodState } from './mood';
import type { Quality } from './post';

/**
 * Art-direction + render-budget glue between the render layer and the vehicles module's mesh
 * factory (the `vehicleMeshFactory` service is world-artist's extension point).
 *
 * Since the second vehicle pass the factory itself does the heavy lifting: one merged body + one
 * glass mesh per car, instanced wheels, distance LODs and the shadow policy (only the body casts).
 * This module feeds it scene facts:
 *  - how dark the mood is (headlight pools and running lamps scale with it),
 *  - how wet the air is (volumetric beams read stronger in rain; rain film on the paint),
 *  - the quality tier (clearcoat on medium/high, volumetric beams on high only, and the body
 *    detail tier: HIGH gets the full sculpt near the camera, MEDIUM/LOW the lighter mid body).
 */
export { lodCamera, lodView };
export { flushWheelBatches } from '../vehicles/wheelBatch';

export const styledVehicleMeshFactory: VehicleMeshFactory = (v) => defaultVehicleMeshFactory(v);

let lastQuality: Quality | null = null;

/** Called by the render system every frame, before the scene renders. */
export function updateVehicleLook(quality: Quality): void {
  const m = moodState.current;
  // Dusk lamps = 1, overcast day = 0.08: headlights read at dusk, barely by day.
  vehicleAmbient.night = Math.min(1, Math.max(0, (m.lamps - 0.05) / 0.95));
  // The road pools follow how much sky is left as well: the dusk sun (2.3) still lights the road, the
  // night moon (1.2) does not, so dusk pools sit at half strength and night pools at full.
  vehicleAmbient.pools = Math.min(1, Math.max(0.5, (2.6 - m.sun.intensity) / 1.2));
  const w = weatherState.current;
  vehicleAmbient.wet = w === 'rain' ? 1 : w === 'drizzle' ? 0.5 : 0.1;
  if (quality !== lastQuality) {
    lastQuality = quality;
    vehicleAmbient.beams = quality === 'high';
    // Body detail tier: HIGH draws the full sculpt near the camera, MEDIUM and LOW the mid body.
    vehicleAmbient.tier = quality === 'high' ? 2 : quality === 'medium' ? 1 : 0;
    setCarClearcoat(quality !== 'low');
  }
}
